import { FC, useCallback, useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import { nivelConsultorService } from '@/services/nivelConsultorService'
import { AVISO_NIVEL_INFORMATIVO, NivelResumido } from '@/types/nivelConsultor'
import { ChipNivel } from './ChipNivel'

type Estado = 'carregando' | 'pronto' | 'erro'

/**
 * Card "Meu nível" do Perfil (AN-10 §10) — só para a persona CONSULTOR (quem renderiza decide).
 * Mostra só nome, cor e descrição (Q-AN10-4) e o aviso fixo "informativo"; sem nível →
 * "Nenhum nível atribuído" (Q-AN10-1).
 */
export const CardMeuNivel: FC = () => {
  const [estado, setEstado] = useState<Estado>('carregando')
  const [nivel, setNivel] = useState<NivelResumido | null>(null)

  const carregar = useCallback(async () => {
    setEstado('carregando')
    try {
      setNivel(await nivelConsultorService.meu())
      setEstado('pronto')
    } catch {
      setNivel(null)
      setEstado('erro')
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  return (
    <Paper sx={{ p: 3 }} aria-label="Meu nível">
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <WorkspacePremiumIcon color="action" />
        <Typography fontWeight={600}>Meu nível</Typography>
      </Stack>
      {estado === 'carregando' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : estado === 'erro' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void carregar()}>
              Tentar novamente
            </Button>
          }
        >
          Não foi possível carregar seu nível.
        </Alert>
      ) : nivel ? (
        <Stack spacing={1.5}>
          <Box>
            <ChipNivel nome={nivel.nome} cor={nivel.cor} size="medium" />
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {nivel.descricao}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {AVISO_NIVEL_INFORMATIVO}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Nenhum nível atribuído
        </Typography>
      )}
    </Paper>
  )
}

export default CardMeuNivel
